import React, { useState } from 'react';

const css = `
  * { box-sizing: border-box; }
  html { font-size: 133%; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-thumb { background: rgba(212,175,55,0.2); border-radius: 2px; }
`;

const S = {
  anton: { fontFamily: "'Anton', sans-serif" },
  mono: { fontFamily: "'Monda', sans-serif" },
};

const LAST_UPDATED = 'April 2026';

// ── Legal Modal ──────────────────────────────────────────────────────
export const LegalModal = ({ page, onClose }) => {
  if (!page) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
      onClick={onClose}>
      <div style={{ background: 'rgba(8,8,8,0.99)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', width: '100%', maxWidth: '720px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        onClick={function(e) { e.stopPropagation(); }}>
        <style>{css}</style>
        <div style={{ height: '3px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)', flexShrink: 0 }} />
        <div style={{ padding: '16px 24px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ ...S.anton, fontSize: '0.9rem', letterSpacing: '2px', color: '#d4af37' }}>{page.title.toUpperCase()}</div>
          <button onClick={onClose} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.5)', borderRadius: '5px', width: '28px', height: '28px', cursor: 'pointer', fontSize: '0.8rem' }}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px' }}>
          {page.content}
        </div>
        <div style={{ padding: '12px 24px', borderTop: '1px solid rgba(255,255,255,0.07)', flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ ...S.mono, fontSize: '0.38rem', color: 'rgba(255,255,255,0.25)' }}>Last updated: {LAST_UPDATED}</div>
          <button onClick={onClose} style={{ ...S.mono, fontSize: '0.44rem', background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.4)', color: '#d4af37', padding: '6px 18px', borderRadius: '5px', cursor: 'pointer' }}>CLOSE</button>
        </div>
      </div>
    </div>
  );
};

// ── Shared styles for legal content ──────────────────────────────────
const H2 = ({ children }) => (
  <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.75rem', letterSpacing: '2px', color: '#d4af37', marginBottom: '8px', marginTop: '20px' }}>
    {children}
  </div>
);
const P = ({ children }) => (
  <p style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.48rem', color: 'rgba(255,255,255,0.65)', lineHeight: '1.7', marginBottom: '10px' }}>
    {children}
  </p>
);
const Li = ({ children }) => (
  <li style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.48rem', color: 'rgba(255,255,255,0.65)', lineHeight: '1.7', marginBottom: '4px', marginLeft: '16px' }}>
    {children}
  </li>
);
const Divider = () => <div style={{ height: '1px', background: 'rgba(255,255,255,0.07)', margin: '16px 0' }} />;

// ── CGU Content ──────────────────────────────────────────────────────
const CGUContent = () => (
  <div>
    <P>Welcome to <strong style={{ color: '#fff' }}>GM Rivalry Engine</strong>. By using this application, you agree to these Terms of Service. Please read them carefully before using the service.</P>
    <Divider />

    <H2>1. SERVICE OVERVIEW</H2>
    <P>GM Rivalry Engine is a web-based wrestling show management simulation inspired by the MyGM mode in WWE 2K. It is provided for entertainment purposes only and is not affiliated with WWE, 2K Games, or Take-Two Interactive.</P>

    <H2>2. ACCESS</H2>
    <P>Access to GM Rivalry Engine requires user account authentication. You are responsible for keeping your credentials confidential. Any activity carried out through your account is your responsibility.</P>
    <P>We reserve the right to suspend or terminate access for any user who violates these Terms, without prior notice.</P>

    <H2>3. PERMITTED USE</H2>
    <P>You agree to use the service in accordance with applicable law and not to:</P>
    <ul>
      <Li>Attempt to bypass the application's security mechanisms</Li>
      <Li>Access other users' data without authorization</Li>
      <Li>Reproduce, distribute, or commercially exploit the service without prior agreement</Li>
      <Li>Use the service for unlawful or harmful purposes</Li>
    </ul>

    <H2>4. INTELLECTUAL PROPERTY</H2>
    <P>GM Rivalry Engine, its source code, design, and features are the property of its developer. Names, logos, and assets related to WWE, WWE 2K, and their respective rights holders belong to their respective owners.</P>
    <P>GM Rivalry Engine is a non-commercial fan-made project. It is not endorsed, sponsored by, or affiliated with WWE, 2K Games, or Take-Two Interactive Software.</P>

    <H2>5. USER DATA</H2>
    <P>Game data (saves, rosters, results) is stored on Firebase servers for the purpose of running the service. See our Privacy Policy for more details.</P>

    <H2>6. LIMITATION OF LIABILITY</H2>
    <P>GM Rivalry Engine is provided "as is", without warranty of any kind. We shall not be liable for data loss, service interruptions, or any damages resulting from the use of the application.</P>

    <H2>7. CHANGES</H2>
    <P>We reserve the right to modify these Terms at any time. Users will be notified of significant changes. Continued use of the service after changes constitutes acceptance of the new terms.</P>

    <H2>8. GOVERNING LAW</H2>
    <P>These Terms are governed by applicable law. Any disputes relating to their interpretation or enforcement shall be submitted to the competent courts.</P>
  </div>
);

// ── Privacy Policy Content ───────────────────────────────────────────
const PrivacyContent = () => (
  <div>
    <P>This Privacy Policy describes how GM Rivalry Engine collects, uses, and protects your personal data.</P>
    <Divider />

    <H2>1. DATA COLLECTED</H2>
    <P>We only collect data necessary for the service to function:</P>
    <ul>
      <Li><strong style={{ color: 'rgba(255,255,255,0.85)' }}>Account data:</strong> email address, user ID (via Firebase Authentication)</Li>
      <Li><strong style={{ color: 'rgba(255,255,255,0.85)' }}>Game data:</strong> saved games, rosters, show results, rivalry history</Li>
      <Li><strong style={{ color: 'rgba(255,255,255,0.85)' }}>Technical data:</strong> connection logs, session information (for security purposes)</Li>
    </ul>
    <P>We do not collect payment data, phone numbers, or sensitive personal information.</P>

    <H2>2. PURPOSE OF PROCESSING</H2>
    <P>Your data is used to:</P>
    <ul>
      <Li>Run the service and save your game progress</Li>
      <Li>Authenticate your access to the service</Li>
      <Li>Improve application features</Li>
      <Li>Ensure security and prevent abuse</Li>
    </ul>

    <H2>3. LEGAL BASIS</H2>
    <P>Processing is based on your consent (at registration) and on the execution of the service you requested.</P>

    <H2>4. STORAGE & SECURITY</H2>
    <P>Your data is stored on Google Firebase servers. We apply reasonable technical measures to protect your data.</P>

    <H2>5. RETENTION</H2>
    <P>Your game data is kept as long as your account is active. Upon account deletion, your data is erased within 30 days.</P>

    <H2>6. YOUR RIGHTS</H2>
    <P>You have the following rights regarding your personal data:</P>
    <ul>
      <Li><strong style={{ color: 'rgba(255,255,255,0.85)' }}>Access:</strong> obtain a copy of your personal data</Li>
      <Li><strong style={{ color: 'rgba(255,255,255,0.85)' }}>Rectification:</strong> correct inaccurate data</Li>
      <Li><strong style={{ color: 'rgba(255,255,255,0.85)' }}>Erasure:</strong> request deletion of your data</Li>
      <Li><strong style={{ color: 'rgba(255,255,255,0.85)' }}>Portability:</strong> receive your data in a structured format</Li>
      <Li><strong style={{ color: 'rgba(255,255,255,0.85)' }}>Objection:</strong> object to certain types of processing</Li>
    </ul>
    <P>To exercise these rights, contact us directly via the application.</P>

    <H2>7. COOKIES</H2>
    <P>The application only uses strictly necessary technical cookies for the service to function (authentication session). No advertising or third-party tracking cookies are used.</P>

    <H2>8. CONTACT</H2>
    <P>For any questions about your personal data or to exercise your rights, contact us directly via the application.</P>
  </div>
);

// ── Disclaimer Content ───────────────────────────────────────────────
const DisclaimerContent = () => (
  <div>
    <div style={{ padding: '14px 16px', background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.25)', borderRadius: '8px', marginBottom: '16px' }}>
      <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.65rem', letterSpacing: '2px', color: '#e74c3c', marginBottom: '6px' }}>⚠ FAN-MADE — UNOFFICIAL PROJECT</div>
      <p style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: 'rgba(255,255,255,0.6)', lineHeight: '1.6' }}>
        GM Rivalry Engine is a non-commercial fan-made project, not affiliated with or endorsed by WWE, 2K Games, or Take-Two Interactive Software.
      </p>
    </div>

    <H2>THIRD-PARTY INTELLECTUAL PROPERTY</H2>
    <P>The following elements present in this application belong to their respective owners:</P>
    <ul>
      <Li><strong style={{ color: 'rgba(255,255,255,0.85)' }}>WWE®:</strong> names, logos, superstar images, PPV/PLE names, championship titles — property of World Wrestling Entertainment, Inc.</Li>
      <Li><strong style={{ color: 'rgba(255,255,255,0.85)' }}>WWE 2K®:</strong> MyGM concept, inspired game mechanics — property of 2K Games / Take-Two Interactive Software, Inc.</Li>
      <Li><strong style={{ color: 'rgba(255,255,255,0.85)' }}>Other brands:</strong> WCW®, ECW®, and other referenced wrestling brands belong to their respective holders</Li>
    </ul>

    <Divider />

    <H2>PROJECT NATURE</H2>
    <P>GM Rivalry Engine is a simulation application created by and for professional wrestling fans. This project:</P>
    <ul>
      <Li>Is not an official WWE or 2K Games product</Li>
      <Li>Is not marketed or sold as such</Li>
      <Li>Is not endorsed, sponsored, or approved by WWE, 2K Games, or their affiliates</Li>
      <Li>Is developed for personal and community entertainment purposes</Li>
    </ul>

    <H2>USE OF ASSETS</H2>
    <P>Images, logos, and other visual elements related to WWE superstars, championships, and events are used under fair use for non-commercial entertainment purposes. If you are a rights holder and wish to have any element removed, contact us and we will act immediately.</P>

    <H2>NO COMMERCIAL AFFILIATION</H2>
    <P>GM Rivalry Engine generates no direct revenue related to WWE or 2K Games intellectual property. Any financial support (e.g. Patreon) exclusively compensates the development work of the tool, not access to third-party proprietary content.</P>

    <Divider />
    <P style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.35)', fontFamily: "'Monda',sans-serif", fontSize: '0.42rem' }}>
      WWE™, SmackDown™, Raw™, NXT™, WrestleMania™ and all associated names are registered trademarks of World Wrestling Entertainment, Inc. WWE 2K™ is a registered trademark of 2K Games / Take-Two Interactive Software, Inc. All rights reserved to their respective owners.
    </P>
  </div>
);

// ── Pages definitions ────────────────────────────────────────────────
export const LEGAL_PAGES = {
  cgu: {
    title: 'Terms of Service',
    shortTitle: 'Terms',
    content: <CGUContent />,
  },
  privacy: {
    title: 'Privacy Policy',
    shortTitle: 'Privacy',
    content: <PrivacyContent />,
  },
  disclaimer: {
    title: 'Disclaimer & Legal Notice',
    shortTitle: 'Disclaimer',
    content: <DisclaimerContent />,
  },
};

// ── Legal Footer Links ───────────────────────────────────────────────
export const LegalFooter = ({ onOpen }) => (
  <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 50, display: 'flex', gap: '16px', alignItems: 'center', justifyContent: 'center', padding: '6px 0', flexWrap: 'wrap', background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
    {Object.entries(LEGAL_PAGES).map(([key, page]) => (
      <button key={key} onClick={() => onOpen(key)}
        style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: 'rgba(255,255,255,0.3)', background: 'none', border: 'none', cursor: 'pointer', letterSpacing: '1px', padding: '2px 0', borderBottom: '1px solid transparent', transition: 'background 0.15s, border-color 0.15s, color 0.15s' }}
        onMouseEnter={function(e) { e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; e.currentTarget.style.borderBottomColor = 'rgba(255,255,255,0.3)'; }}
        onMouseLeave={function(e) { e.currentTarget.style.color = 'rgba(255,255,255,0.3)'; e.currentTarget.style.borderBottomColor = 'transparent'; }}>
        {page.shortTitle.toUpperCase()}
      </button>
    ))}
    <div style={{ width: '1px', height: '10px', background: 'rgba(255,255,255,0.1)' }} />
    <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.36rem', color: 'rgba(255,255,255,0.15)', letterSpacing: '1px' }}>
      © 2026 GM RIVALRY ENGINE — A THEIMMORTAL21 PROJECT
    </div>
  </div>
);

// ── Hook for easy usage ──────────────────────────────────────────────
export function useLegal() {
  const [activePage, setActivePage] = React.useState(null);
  const openPage = function(key) { setActivePage(key); };
  const closePage = function() { setActivePage(null); };
  const modal = activePage ? (
    <LegalModal page={LEGAL_PAGES[activePage]} onClose={closePage} />
  ) : null;
  return { openPage, closePage, modal };
}
